// Painting one string with two independent layers: emphasis from the
// Manuscript's markers, and removal from the word diff. Both are ranges over
// the same stripped text, they overlap freely, and React needs a flat list of
// pieces, so the boundaries of both layers are merged into one walk.

import type { EmphasisKind, EmphasisRun } from "./emphasis";

/** A half-open range over a string. */
export interface Range {
  readonly start: number;
  readonly end: number;
}

/** One piece of text with everything that is true of all of it. */
export interface Segment {
  readonly text: string;
  readonly emphasis: EmphasisKind;
  /** The word diff says this run is not in the Narration Script. */
  readonly removed: boolean;
}

const covers = (ranges: readonly Range[], at: number): boolean =>
  ranges.some((r) => r.start <= at && at < r.end);

/**
 * Cuts `text` at every boundary either layer introduces and reports what is
 * true of each piece. Ranges outside the text are ignored rather than clamped,
 * so a stale diff cached against an older text cannot paint past its end.
 */
export function paintSegments(
  text: string,
  runs: readonly EmphasisRun[],
  removals: readonly Range[],
): Segment[] {
  if (text === "") return [];
  const cuts = new Set<number>([0, text.length]);
  for (const layer of [runs, removals]) {
    for (const r of layer) {
      if (r.start > 0 && r.start < text.length) cuts.add(r.start);
      if (r.end > 0 && r.end < text.length) cuts.add(r.end);
    }
  }
  const ordered = [...cuts].sort((a, b) => a - b);
  const segments: Segment[] = [];
  for (let i = 0; i < ordered.length - 1; i++) {
    const start = ordered[i] as number;
    const end = ordered[i + 1] as number;
    const run = runs.find((r) => r.start <= start && start < r.end);
    segments.push({
      text: text.slice(start, end),
      emphasis: run?.kind ?? "none",
      removed: covers(removals, start),
    });
  }
  return segments;
}

/** Shifts ranges from whole-source coordinates into one node's, dropping what falls outside. */
export function rangesWithin(ranges: readonly Range[], span: Range): Range[] {
  const out: Range[] = [];
  for (const range of ranges) {
    const start = Math.max(range.start, span.start);
    const end = Math.min(range.end, span.end);
    if (start < end) out.push({ start: start - span.start, end: end - span.start });
  }
  return out;
}
